import React, { useState, useMemo, useCallback, useRef, useEffect } from 'react'
import { useAuthStore } from '../store/authStore'
import { useAdminScope } from '../hooks/useAdminScope'
import { supabaseClient } from '../config/supabase'
import { type PeoAlignmentRow, type TracerStudyStats } from '../hooks/useAnalytics'
import {
  generatePdfReport,
  generateExcelReport,
  downloadPdf,
  downloadExcel,
  type ReportConfig,
  type ReportSection,
} from '../utils/exportAnalytics'
import {
  FileText, Download, Target, TrendingUp, FileBarChart, Lightbulb,
  Users, Briefcase, Building2, ClipboardList, BarChart3,
  Loader2, Filter, ChevronDown, Check
} from 'lucide-react'
import toast from 'react-hot-toast'

// ── Logo loader ───────────────────────────────────────────────
async function loadImageAsDataUrl(url: string): Promise<string | undefined> {
  try {
    const res = await fetch(url)
    const blob = await res.blob()
    return await new Promise<string>((resolve, reject) => {
      const reader = new FileReader()
      reader.onload = () => resolve(reader.result as string)
      reader.onerror = reject
      reader.readAsDataURL(blob)
    })
  } catch {
    return undefined
  }
}

// ── Report Type Definitions ───────────────────────────────────
interface ReportType {
  id: string
  label: string
  description: string
  icon: React.ElementType
  category: 'analytics' | 'database'
}

const REPORT_TYPES: ReportType[] = [
  // Analytics Reports
  { id: 'peo_alignment', label: 'PEO/PO Alignment Report', description: 'Outcome scores, attainment rates, and color-coded score heatmap per program', icon: Target, category: 'analytics' },
  { id: 'tracer_study', label: 'Tracer Study Report', description: 'Employment rate, salary, job relevance, employer type, and trends', icon: TrendingUp, category: 'analytics' },
  { id: 'comprehensive', label: 'Comprehensive Analytics Report', description: 'All DSS analytics sections combined into one document', icon: FileBarChart, category: 'analytics' },
  { id: 'insights', label: 'Insights Report', description: 'AI-generated strengths, weaknesses, PEO analysis, and action items', icon: Lightbulb, category: 'analytics' },
  // Database Reports
  { id: 'alumni_directory', label: 'Alumni Directory Report', description: 'Full alumni listing with name, program, graduation year, employment status, contact info', icon: Users, category: 'database' },
  { id: 'employment_status', label: 'Employment Status Report', description: 'Alumni grouped by employment status with counts per program', icon: Briefcase, category: 'database' },
  { id: 'employer_partnership', label: 'Employer & Partnership Report', description: 'Registered companies, company size, and industry', icon: Building2, category: 'database' },
  { id: 'survey_responses', label: 'Survey Responses Report', description: 'Aggregated survey analytics with rating distributions, choice breakdowns, and text responses', icon: ClipboardList, category: 'database' },
  { id: 'job_market', label: 'Job Market Report', description: 'Job postings, application counts, and hire rate per job', icon: BarChart3, category: 'database' },
]

const SALARY_LABELS: Record<string, string> = {
  under_20k: 'Under ₱20k', '20k_40k': '₱20k-40k', '40k_60k': '₱40k-60k',
  '60k_80k': '₱60k-80k', '80k_100k': '₱80k-100k', over_100k: 'Over ₱100k',
}

const STATUS_LABELS: Record<string, string> = {
  employed: 'Employed', unemployed: 'Unemployed', 'self-employed': 'Self-Employed',
  studying: 'Studying',
}

// ── Survey custom dropdown ─────────────────────────────────────
interface SurveyDropdownProps {
  surveys: { id: string; title: string }[]
  value: string | null
  onChange: (v: string | null) => void
}
const SurveyDropdown: React.FC<SurveyDropdownProps> = ({ surveys, value, onChange }) => {
  const [open, setOpen] = useState(false)
  const ref = useRef<HTMLDivElement>(null)
  const selected = surveys.find(s => s.id === value)

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false)
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [])

  return (
    <div ref={ref} className="relative">
      <label className="text-xs font-medium text-gray-500 mb-1 block">
        Survey <span className="text-red-500">*</span>
      </label>
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm text-left flex items-center gap-2 bg-white hover:border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500"
      >
        <span className="flex-1 truncate text-gray-700">
          {selected ? selected.title : <span className="text-gray-400">Select a survey</span>}
        </span>
        <ChevronDown className={`w-4 h-4 text-gray-400 shrink-0 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && (
        <div className="absolute z-50 bottom-full mb-1 left-0 right-0 bg-white border border-gray-200 rounded-lg shadow-lg max-h-56 overflow-y-auto">
          <button
            type="button"
            onClick={() => { onChange(null); setOpen(false) }}
            className="w-full text-left px-3 py-2 text-sm text-gray-400 hover:bg-gray-50"
          >
            Select a survey
          </button>
          {surveys.map(s => (
            <button
              key={s.id}
              type="button"
              onClick={() => { onChange(s.id); setOpen(false) }}
              className={`w-full text-left px-3 py-2 text-sm flex items-start gap-2 hover:bg-blue-50 ${s.id === value ? 'bg-blue-50 text-blue-900 font-medium' : 'text-gray-700'}`}
            >
              <Check className={`w-4 h-4 mt-0.5 shrink-0 ${s.id === value ? 'text-blue-900' : 'invisible'}`} />
              <span className="flex-1">{s.title}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  )
}

// ── Component ─────────────────────────────────────────────────
const ReportsPage: React.FC = () => {
  const { user } = useAuthStore()
  const { isSuperAdmin, department, loading: scopeLoading } = useAdminScope()

  const [selectedReport, setSelectedReport] = useState<string | null>(null)
  const [format, setFormat] = useState<'pdf' | 'excel'>('pdf')
  const [generating, setGenerating] = useState(false)

  // Filters
  const [deptFilter, setDeptFilter] = useState<string | null>(null)
  const [progFilter, setProgFilter] = useState<string | null>(null)
  const [batchFilter, setBatchFilter] = useState<number | null>(null)
  const [surveyFilter, setSurveyFilter] = useState<string | null>(null)

  // Dropdowns
  const [departments, setDepartments] = useState<string[]>([])
  const [programs, setPrograms] = useState<{ code: string; department: string }[]>([])
  const [batchYears, setBatchYears] = useState<number[]>([])
  const [surveys, setSurveys] = useState<{ id: string; title: string }[]>([])

  // Fetch dropdown data
  React.useEffect(() => {
    const fetch = async () => {
      const { data: deptData } = await supabaseClient.from('departments').select('name').eq('is_active', true).order('name')
      if (deptData) setDepartments(deptData.map(d => d.name))

      const { data: progData } = await supabaseClient.from('programs').select('code, departments!inner(name)').eq('is_active', true).order('code')
      if (progData) setPrograms(progData.map((p: any) => ({ code: p.code, department: p.departments?.name || '' })))

      const { data: yearData } = await supabaseClient.from('alumni_profiles').select('year_graduated').eq('approval_status', 'approved').not('year_graduated', 'is', null).order('year_graduated', { ascending: false })
      if (yearData) setBatchYears([...new Set(yearData.map(r => r.year_graduated).filter(Boolean))] as number[])

    }
    fetch()
  }, [])

  // Initialize dept filter for dept admins
  React.useEffect(() => {
    if (!scopeLoading && !isSuperAdmin && department) setDeptFilter(department)
  }, [scopeLoading, isSuperAdmin, department])

  // Fetch surveys with responses — scoped to dept for non-super admins
  React.useEffect(() => {
    if (scopeLoading) return
    const fetchSurveys = async () => {
      const { data: responseData } = await supabaseClient
        .from('survey_responses')
        .select('survey_id, surveys(id, title, created_at, target_departments)')
      if (responseData) {
        const surveysMap = new Map<string, { id: string; title: string; created_at: string; target_departments: string[] | null }>()
        responseData.forEach((r: any) => {
          if (r.surveys) {
            surveysMap.set(r.surveys.id, {
              id: r.surveys.id,
              title: r.surveys.title,
              created_at: r.surveys.created_at,
              target_departments: r.surveys.target_departments,
            })
          }
        })
        let uniqueSurveys = Array.from(surveysMap.values()).sort((a, b) =>
          new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
        )
        // Dept admins only see surveys targeting their department
        if (!isSuperAdmin && department) {
          uniqueSurveys = uniqueSurveys.filter(s =>
            !s.target_departments || s.target_departments.length === 0 || s.target_departments.includes(department)
          )
        }
        setSurveys(uniqueSurveys.map(s => ({ id: s.id, title: s.title })))
      }
    }
    fetchSurveys()
  }, [scopeLoading, isSuperAdmin, department])

  const effectiveDept = isSuperAdmin ? deptFilter : department
  const filteredPrograms = useMemo(() => {
    if (!effectiveDept) return programs
    return programs.filter(p => p.department === effectiveDept)
  }, [programs, effectiveDept])

  const selectedReportDef = REPORT_TYPES.find(r => r.id === selectedReport)
  const analyticsReports = REPORT_TYPES.filter(r => r.category === 'analytics')
  const databaseReports = REPORT_TYPES.filter(r =>
    r.category === 'database' && (isSuperAdmin || r.id !== 'job_market')
  )

  // ── Report config ──────────────────────────────────────────
  const adminName = useMemo(() => {
    const profile = user?.profile as any
    const firstName = profile?.first_name || ''
    const lastName = profile?.last_name || ''
    const fullName = [firstName, lastName].filter(Boolean).join(' ')
    return fullName || null
  }, [user])

  const buildConfig = useCallback((): ReportConfig => ({
    title: selectedReportDef?.label || 'Report',
    subtitle: [effectiveDept, progFilter, batchFilter ? `Class of ${batchFilter}` : null].filter(Boolean).join(' · ') || undefined,
    department: effectiveDept || undefined,
    program: progFilter || undefined,
    batchYear: batchFilter,
    preparedBy: adminName
      ? `${adminName} (${user?.email || ''})`
      : (user?.email || 'Admin'),
    dateGenerated: new Date().toLocaleDateString('en-PH', { year: 'numeric', month: 'long', day: 'numeric' }),
    // logos are injected at generate time
  }), [selectedReportDef, effectiveDept, progFilter, batchFilter, user, adminName])

  // ── Build sections for PEO report ──────────────────────────
  const buildPeoSections = (data: PeoAlignmentRow[]): ReportSection[] => {
    const programs = [...new Set(data.map(r => r.program_code))]
    const sections: ReportSection[] = []

    // Summary KPIs
    const allScores = data.map(r => r.avg_score)
    const avgScore = allScores.length > 0 ? Math.round(allScores.reduce((a, b) => a + b, 0) / allScores.length) : 0
    const allAttainment = data.map(r => r.attainment_rate).filter(r => r != null)
    const avgAttainment = allAttainment.length > 0 ? Math.round(allAttainment.reduce((a, b) => a + b, 0) / allAttainment.length) : 0

    sections.push({
      title: 'PEO/PO Alignment Summary',
      type: 'kpi',
      kpis: [
        { label: 'Overall Avg Attainment Rate (% of alumni scoring >=70)', value: avgAttainment, unit: '%' },
        { label: 'Overall Avg Outcome Score (out of 100)', value: avgScore, unit: '' },
        { label: 'Programs Assessed', value: programs.length },
        { label: 'Total Outcome Records', value: data.length },
      ],
    })

    // PEO scores aggregated by program
    const peoRows = data.filter(r => r.outcome_type === 'PEO')
    if (peoRows.length > 0) {
      const peoPrograms = [...new Set(peoRows.map(r => r.program_code))].sort()
      const peoNums = [...new Set(peoRows.map(r => r.peo_number))].sort((a, b) => a - b)

      const peoMap = new Map<string, { scores: number[]; attainment: number[]; resp: number[] }>()
      peoRows.forEach(r => {
        if (!peoMap.has(r.program_code)) peoMap.set(r.program_code, { scores: [], attainment: [], resp: [] })
        const e = peoMap.get(r.program_code)!
        e.scores.push(r.avg_score)
        e.attainment.push(r.attainment_rate)
        e.resp.push(r.respondent_count)
      })
      sections.push({
        title: 'PEO Scores by Program',
        type: 'table',
        description: 'Average Program Educational Outcome (PEO) scores per program — describes what graduates are expected to achieve years after graduation.',
        data: {
          headers: ['Program', 'Avg PEO Score (out of 100)', 'Avg Attainment Rate (%)', 'Avg Respondents'],
          rows: Array.from(peoMap.entries()).map(([prog, e]) => [
            prog,
            Math.round(e.scores.reduce((a, b) => a + b, 0) / e.scores.length),
            Math.round(e.attainment.reduce((a, b) => a + b, 0) / e.attainment.length),
            Math.round(e.resp.reduce((a, b) => a + b, 0) / e.resp.length),
          ]),
        },
      })

      // PEO heatmap: rows = programs, cols = PEO 1, PEO 2, ...
      sections.push({
        title: 'PEO Score Heatmap',
        type: 'heatmap',
        description: 'Color-coded score matrix per program and outcome. Green = >=70 (Met), Yellow = 50-69 (Partial), Red = <50 (Needs Work), Gray = N/A.',
        data: {
          headers: ['Program', ...peoNums.map(n => `PEO ${n}`)],
          rows: peoPrograms.map(prog => [
            prog,
            ...peoNums.map(n => {
              const row = peoRows.find(r => r.program_code === prog && r.peo_number === n)
              return row ? row.avg_score : null
            }),
          ]),
        },
      })
    }

    // PO scores aggregated by program
    const poRows = data.filter(r => r.outcome_type === 'PO')
    if (poRows.length > 0) {
      const poPrograms = [...new Set(poRows.map(r => r.program_code))].sort()
      const poNums = [...new Set(poRows.map(r => r.peo_number))].sort((a, b) => a - b)

      const poMap = new Map<string, { scores: number[]; attainment: number[]; resp: number[] }>()
      poRows.forEach(r => {
        if (!poMap.has(r.program_code)) poMap.set(r.program_code, { scores: [], attainment: [], resp: [] })
        const e = poMap.get(r.program_code)!
        e.scores.push(r.avg_score)
        e.attainment.push(r.attainment_rate)
        e.resp.push(r.respondent_count)
      })
      sections.push({
        title: 'PO Scores by Program',
        type: 'table',
        description: 'Average Program Outcome (PO) scores per program — describes the knowledge, skills, and attitudes students are expected to demonstrate at graduation.',
        data: {
          headers: ['Program', 'Avg PO Score (out of 100)', 'Avg Attainment Rate (%)', 'Avg Respondents'],
          rows: Array.from(poMap.entries()).map(([prog, e]) => [
            prog,
            Math.round(e.scores.reduce((a, b) => a + b, 0) / e.scores.length),
            Math.round(e.attainment.reduce((a, b) => a + b, 0) / e.attainment.length),
            Math.round(e.resp.reduce((a, b) => a + b, 0) / e.resp.length),
          ]),
        },
      })

      // PO heatmap: rows = programs, cols = PO 1, PO 2, ...
      sections.push({
        title: 'PO Score Heatmap',
        type: 'heatmap',
        description: 'Color-coded score matrix per program and outcome. Green = >=70 (Met), Yellow = 50-69 (Partial), Red = <50 (Needs Work), Gray = N/A.',
        data: {
          headers: ['Program', ...poNums.map(n => `PO ${n}`)],
          rows: poPrograms.map(prog => [
            prog,
            ...poNums.map(n => {
              const row = poRows.find(r => r.program_code === prog && r.peo_number === n)
              return row ? row.avg_score : null
            }),
          ]),
        },
      })
    }

    return sections
  }

  // ── Build sections for tracer study ────────────────────────
  const buildTracerSections = (data: TracerStudyStats): ReportSection[] => {
    const sections: ReportSection[] = []

    sections.push({
      title: 'Employment Overview',
      type: 'kpi',
      kpis: [
        { label: 'Total Alumni', value: data.total_alumni },
        { label: 'Total Employed', value: data.total_employed },
        { label: 'Employment Rate', value: data.employment_rate, unit: '%' },
      ],
    })

    // Employment by status
    if (data.employment_by_status) {
      sections.push({
        title: 'Employment Status Distribution',
        type: 'table',
        data: {
          headers: ['Status', 'Count', 'Percentage'],
          rows: Object.entries(data.employment_by_status).map(([k, v]) => [
            STATUS_LABELS[k] || k, v, `${data.total_alumni > 0 ? Math.round(v / data.total_alumni * 100) : 0}%`,
          ]),
        },
      })
    }

    // Salary distribution
    if (data.salary_distribution) {
      sections.push({
        title: 'Salary Distribution',
        type: 'table',
        data: {
          headers: ['Salary Range', 'Count'],
          rows: Object.entries(data.salary_distribution).map(([k, v]) => [SALARY_LABELS[k] || k, v]),
        },
      })
    }

    // Job relevance
    if (data.job_relevance_distribution) {
      sections.push({
        title: 'Job Relevance Distribution',
        type: 'table',
        data: {
          headers: ['Relevance', 'Count'],
          rows: Object.entries(data.job_relevance_distribution).map(([k, v]) => [
            k.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()), v,
          ]),
        },
      })
    }

    // Top fields
    if (data.top_fields_of_work?.length) {
      sections.push({
        title: 'Top Fields of Work',
        type: 'table',
        data: {
          headers: ['Field', 'Count'],
          rows: data.top_fields_of_work.map(f => [f.field, f.count]),
        },
      })
    }

    // Top skills
    if (data.top_skills?.length) {
      sections.push({
        title: 'Top Skills Acquired',
        type: 'table',
        data: {
          headers: ['Skill', 'Count'],
          rows: data.top_skills.map(s => [s.skill, s.count]),
        },
      })
    }

    return sections
  }

  // ── Generate Report Handler ────────────────────────────────
  const handleGenerate = async () => {
    if (!selectedReport) return
    setGenerating(true)

    try {
      const config = buildConfig()

      // Pre-load logos for PDF
      if (format === 'pdf') {
        const [ucUrl, tbUrl] = await Promise.all([
          loadImageAsDataUrl('/uc-logo.png'),
          loadImageAsDataUrl('/trailblaze2_logonobg.png'),
        ])
        config.ucLogoDataUrl = ucUrl
        config.trailblazeLogoDataUrl = tbUrl
      }
      let sections: ReportSection[] = []
      let interventionsList: { severity: string; category: string; program: string; message: string }[] | undefined

      // ── Analytics report data ──────────────────────────
      if (['peo_alignment', 'comprehensive', 'insights'].includes(selectedReport)) {
        const { data: peoData, error: peoErr } = await supabaseClient.rpc('get_peo_alignment_stats', {
          p_department: effectiveDept || null,
          p_program: progFilter || null,
          p_batch_year: batchFilter || null,
        })
        if (peoErr) throw peoErr

        if (selectedReport === 'peo_alignment' || selectedReport === 'comprehensive') {
          if (peoData && peoData.length > 0) {
            sections.push(...buildPeoSections(peoData))
          }
        }

        // ── AI Insights report ──────────────────────────
        if (selectedReport === 'insights' || selectedReport === 'comprehensive') {
          const { data: tracerData } = await supabaseClient.rpc('get_tracer_study_stats', {
            p_department: effectiveDept || null,
            p_program: progFilter || null,
            p_batch_year: batchFilter || null,
          })
          const { data: trendData } = await supabaseClient.rpc('get_tracer_study_trends', {
            p_department: effectiveDept || null,
          })
          const { data: compData } = await supabaseClient.rpc('get_department_comparison_stats')

          try {
            const { data: { session } } = await supabaseClient.auth.getSession()
            const token = session?.access_token
            if (token) {
              const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:8000'
              const res = await fetch(`${apiUrl}/api/analytics/insights`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
                body: JSON.stringify({
                  department: effectiveDept || null,
                  program: progFilter || null,
                  batch_year: batchFilter || null,
                  peo_data: peoData ?? [],
                  tracer_stats: tracerData ?? null,
                  trends: trendData ?? [],
                  department_comparison: compData ?? [],
                }),
              })
              if (res.ok) {
                const body = await res.json()
                const ai = body.insights
                if (ai) {
                  sections.push({
                    title: 'Executive Summary',
                    type: 'text',
                    content: ai.summary,
                  } as any)

                  if ((ai.strengths ?? []).length > 0) {
                    sections.push({
                      title: 'Strengths',
                      type: 'table',
                      data: {
                        headers: ['Title', 'Detail', 'Metric'],
                        rows: (ai.strengths ?? []).map((s: any) => [s.title, s.detail, s.metric ?? '']),
                      },
                    })
                  }

                  if ((ai.weaknesses ?? []).length > 0) {
                    sections.push({
                      title: 'Weaknesses',
                      type: 'table',
                      data: {
                        headers: ['Title', 'Detail', 'Metric'],
                        rows: (ai.weaknesses ?? []).map((w: any) => [w.title, w.detail, w.metric ?? '']),
                      },
                    })
                  }

                  if ((ai.peo_insights ?? []).length > 0) {
                    sections.push({
                      title: 'PEO/PO Insights',
                      type: 'table',
                      data: {
                        headers: ['Type', 'Number', 'Program', 'Outcome', 'Status', 'Insight', 'Recommendation'],
                        rows: (ai.peo_insights ?? []).map((p: any) => [
                          p.outcome_type ?? '',
                          p.peo_number != null ? `${p.outcome_type ?? ''} ${p.peo_number}` : '',
                          p.program_code ?? '',
                          p.outcome,
                          p.status?.replace(/_/g, ' ') ?? '',
                          p.insight,
                          p.recommendation ?? '',
                        ]),
                      },
                    })
                  }

                  if (ai.employment_insights) {
                    const empRows: string[][] = []
                    if (ai.employment_insights.summary) empRows.push(['Summary', ai.employment_insights.summary])
                    ;(ai.employment_insights.key_findings ?? []).forEach((f: string) => empRows.push(['Finding', f]))
                    ;(ai.employment_insights.recommendations ?? []).forEach((r: string) => empRows.push(['Recommendation', r]))
                    if (empRows.length > 0) {
                      sections.push({
                        title: 'Employment Insights',
                        type: 'table',
                        data: { headers: ['Type', 'Detail'], rows: empRows },
                      })
                    }
                  }

                  if ((ai.action_items ?? []).length > 0) {
                    sections.push({
                      title: 'Action Items',
                      type: 'table',
                      data: {
                        headers: ['Priority', 'Action', 'Rationale'],
                        rows: (ai.action_items ?? []).map((a: any) => [
                          a.priority?.toUpperCase() ?? '', a.action, a.rationale ?? '',
                        ]),
                      },
                    })
                  }
                }
              }
            }
          } catch (aiErr) {
            console.warn('AI insights fetch failed, continuing without insights:', aiErr)
          }
        }
      }

      if (['tracer_study', 'comprehensive'].includes(selectedReport)) {
        const { data: tracerData, error: tracerErr } = await supabaseClient.rpc('get_tracer_study_stats', {
          p_department: effectiveDept || null,
          p_program: progFilter || null,
          p_batch_year: batchFilter || null,
        })
        if (tracerErr) throw tracerErr
        if (tracerData) sections.push(...buildTracerSections(tracerData))
      }

      // ── Database reports ─────────────────────────────────
      if (selectedReport === 'alumni_directory') {
        config.title = 'Alumni Directory Report'
        let query = supabaseClient
          .from('alumni_profiles')
          .select('first_name, last_name, program, department, year_graduated, current_employment_status, field_of_work, phone')
          .eq('approval_status', 'approved')
          .order('last_name')

        if (effectiveDept) query = query.eq('department', effectiveDept)
        if (progFilter) query = query.eq('program', progFilter)
        if (batchFilter) query = query.eq('year_graduated', batchFilter)

        const { data: alumni, error: err } = await query
        if (err) throw err

        const yearCounts = new Map<string, number>()
        ;(alumni || []).forEach((a: any) => {
          const year = String(a.year_graduated || 'Unknown')
          yearCounts.set(year, (yearCounts.get(year) || 0) + 1)
        })
        const sortedYears = Array.from(yearCounts.entries()).sort((a, b) => a[0].localeCompare(b[0]))

        sections.push({
          title: 'Alumni Directory',
          type: 'table',
          data: {
            headers: ['Last Name', 'First Name', 'Program', 'Department', 'Year', 'Status', 'Field', 'Phone'],
            rows: (alumni || []).map((a: any) => [
              a.last_name || '', a.first_name || '', a.program || '', a.department || '',
              a.year_graduated || '', STATUS_LABELS[a.current_employment_status] || a.current_employment_status || '',
              a.field_of_work || '', a.phone || '',
            ]),
          },
          chartOverride: {
            title: 'Alumni by Graduation Year',
            labels: sortedYears.map(([year]) => year),
            values: sortedYears.map(([, count]) => count),
          },
        })
      }

      if (selectedReport === 'employment_status') {
        config.title = 'Employment Status Report'
        let query = supabaseClient
          .from('alumni_profiles')
          .select('program, department, current_employment_status, unemployment_reasons')
          .eq('approval_status', 'approved')

        if (effectiveDept) query = query.eq('department', effectiveDept)
        if (progFilter) query = query.eq('program', progFilter)
        if (batchFilter) query = query.eq('year_graduated', batchFilter)

        const { data: alumni, error: err } = await query
        if (err) throw err

        // Group by program
        const grouped: Record<string, Record<string, number>> = {}
        ;(alumni || []).forEach((a: any) => {
          const prog = a.program || 'Unknown'
          if (!grouped[prog]) grouped[prog] = {}
          const status = a.current_employment_status || 'unknown'
          grouped[prog][status] = (grouped[prog][status] || 0) + 1
        })

        const allStatuses = ['employed', 'self-employed', 'unemployed', 'studying']
        sections.push({
          title: 'Employment by Program',
          type: 'table',
          data: {
            headers: ['Program', ...allStatuses.map(s => STATUS_LABELS[s] || s), 'Total Alumni'],
            rows: Object.entries(grouped).map(([prog, counts]) => {
              const total = Object.values(counts).reduce((a, b) => a + b, 0)
              return [prog, ...allStatuses.map(s => counts[s] || 0), total]
            }),
          },
        })
      }

      if (selectedReport === 'employer_partnership') {
        config.title = 'Employer & Partnership Report'

        const [{ data: empData, error: empErr }, { data: allPartnerData }] = await Promise.all([
          supabaseClient
            .from('employer_profiles')
            .select('company_id, contact_person, phone, is_industry_partner, approval_status, created_at, companies(name, industry), users!employer_profiles_user_id_fkey(email)')
            .neq('approval_status', 'deactivated')
            .order('created_at', { ascending: false }),
          supabaseClient
            .from('company_department_partnerships')
            .select('company_id, status, departments(name)'),
        ])
        if (empErr) throw empErr

        // Build company_id → approved dept names map (for display)
        const deptMap: Record<string, string[]> = {}
        // Build set of company_ids with ANY relationship to effectiveDept (for filtering)
        const companiesWithDeptRelation = new Set<string>()
        for (const row of (allPartnerData || []) as any[]) {
          const cid = row.company_id
          const dname = row.departments?.name
          if (cid && dname) {
            if (row.status === 'approved') {
              if (!deptMap[cid]) deptMap[cid] = []
              if (!deptMap[cid].includes(dname)) deptMap[cid].push(dname)
            }
            // Only approved/pending — rejected means no active relation with this dept
            if (effectiveDept && dname === effectiveDept && row.status !== 'rejected') {
              companiesWithDeptRelation.add(cid)
            }
          }
        }

        // For dept admins: only industry partners with approved/pending relation to this dept
        const filteredEmpData = (!isSuperAdmin && effectiveDept)
          ? (empData || []).filter((r: any) => r.is_industry_partner && companiesWithDeptRelation.has(r.company_id))
          : (empData || [])

        sections.push({
          title: 'Employers & Partnerships',
          type: 'table',
          tableOptions: {
            fontSize: 8,
            cellPadding: 4,
            columnStyles: {
              0: { cellWidth: 65 },  // Company Name
              1: { cellWidth: 58 },  // Contact Person
              2: { cellWidth: 88 },  // Email
              3: { cellWidth: 62 },  // Phone
              4: { cellWidth: 52 },  // Industry
              5: { cellWidth: 44 },  // Industry Partner
              6: { cellWidth: 58 },  // Partner Departments
              7: { cellWidth: 41 },  // Status
            },
          },
          data: {
            headers: ['Company Name', 'Contact Person', 'Email', 'Phone', 'Industry', 'Industry Partner', 'Partner Departments', 'Status'],
            rows: filteredEmpData.map((r: any) => [
              r.companies?.name || '',
              r.contact_person || '',
              (r.users as any)?.email || '',
              r.phone || '',
              r.companies?.industry || '',
              r.is_industry_partner ? 'Yes' : 'No',
              r.is_industry_partner ? (deptMap[r.company_id] || []).join(', ') || '—' : '—',
              r.approval_status ? r.approval_status.charAt(0).toUpperCase() + r.approval_status.slice(1) : '',
            ]),
          },
        })
      }

      if (selectedReport === 'survey_responses') {
        if (!surveyFilter) {
          toast.error('Please select a survey')
          setGenerating(false)
          return
        }

        config.title = 'Survey Responses Report'
        const { data: survey, error: survErr } = await supabaseClient
          .from('surveys')
          .select('id, title, questions')
          .eq('id', surveyFilter)
          .single()
        if (survErr) throw survErr

        const { data: responses, error: resErr } = await supabaseClient
          .from('survey_responses')
          .select('user_id, answers, submitted_at')
          .eq('survey_id', surveyFilter)
        if (resErr) throw resErr

        if (!responses || responses.length === 0) {
          toast.error('No responses found for this survey')
          setGenerating(false)
          return
        }

        // Fetch alumni profiles for the respondents
        const userIds = responses.map((r: any) => r.user_id)
        const { data: alumniProfiles } = await supabaseClient
          .from('alumni_profiles')
          .select('user_id, first_name, last_name, program, department, year_graduated')
          .in('user_id', userIds)

        // Create a map of user_id to alumni profile
        const profileMap = new Map()
        alumniProfiles?.forEach((p: any) => {
          profileMap.set(p.user_id, p)
        })

        // For dept admins: only include responses from alumni in their department
        const scopedResponses = (!isSuperAdmin && effectiveDept)
          ? responses.filter((r: any) => profileMap.get(r.user_id)?.department === effectiveDept)
          : responses

        if (scopedResponses.length === 0) {
          toast.error('No responses from your department found for this survey')
          setGenerating(false)
          return
        }

        config.subtitle = `${survey.title} (${scopedResponses.length} responses)`
        const questions = survey.questions as any[] || []

        // Summary table of all respondents
        const summaryHeaders = ['Respondent', 'Program', 'Department', 'Year', 'Submitted']
        const summaryRows = scopedResponses.map((r: any) => {
          const profile = profileMap.get(r.user_id)
          return [
            profile ? `${profile.first_name || ''} ${profile.last_name || ''}`.trim() : 'N/A',
            profile?.program || 'N/A',
            profile?.department || 'N/A',
            profile?.year_graduated || 'N/A',
            r.submitted_at ? new Date(r.submitted_at).toLocaleDateString('en-PH') : 'N/A',
          ]
        })

        sections.push({
          title: `Respondent Summary`,
          type: 'table',
          data: { headers: summaryHeaders, rows: summaryRows },
        })

        // Process each question with aggregated stats
        questions.forEach((q: any, qIdx: number) => {
          const questionText = q.text || q.question || `Question ${qIdx + 1}`
          const qType = q.type || 'text'

          // Collect all answers for this question
          const allAnswers = scopedResponses.map((r: any) => (r.answers || {})[q.id]).filter((ans: any) => ans !== undefined && ans !== null && ans !== '')

          // Skip questions with no responses
          if (allAnswers.length === 0) return

          if (qType === 'rating') {
            // Aggregate rating data
            const numericAnswers = allAnswers.filter((ans: any) => typeof ans === 'number' || !isNaN(Number(ans))).map(Number)
            if (numericAnswers.length > 0) {
              const avg = numericAnswers.reduce((a, b) => a + b, 0) / numericAnswers.length
              const min = Math.min(...numericAnswers)
              const max = Math.max(...numericAnswers)
              
              // Distribution counts
              const scale = q.rating_scale || 10
              const distribution: Record<number, number> = {}
              for (let i = 1; i <= scale; i++) distribution[i] = 0
              numericAnswers.forEach(n => distribution[n] = (distribution[n] || 0) + 1)

              const distData = Object.entries(distribution).map(([rating, count]) => ({
                label: String(rating),
                count,
                pct: (count / numericAnswers.length) * 100,
              }))

              // Single section: KPIs + embedded distribution bars
              const section: any = {
                title: `Q${qIdx + 1}: ${questionText}`,
                type: 'kpi',
                kpis: [
                  { label: 'Response Count', value: numericAnswers.length },
                  { label: 'Average Rating', value: avg.toFixed(2) },
                  { label: 'Minimum', value: min },
                  { label: 'Maximum', value: max },
                ],
                distribution: distData,
              }
              sections.push(section)
            }
          } else if (qType === 'multiple_choice') {
            // Aggregate multiple choice data
            const choiceCounts: Record<string, number> = {}
            allAnswers.forEach((ans: any) => {
              const choice = String(ans)
              choiceCounts[choice] = (choiceCounts[choice] || 0) + 1
            })

            const barChart = Object.entries(choiceCounts)
              .sort((a, b) => b[1] - a[1])
              .map(([choice, count]) => ({
                label: choice,
                count,
                pct: (count / allAnswers.length) * 100,
              }))

            sections.push({
              title: `Q${qIdx + 1}: ${questionText}`,
              type: 'text',
              barChart,
              totalResponses: allAnswers.length,
            } as any)
          } else if (qType === 'yes_no') {
            // Aggregate Yes/No responses
            let yesCount = 0
            let noCount = 0
            allAnswers.forEach((ans: any) => {
              if (ans === true || ans === 'true' || ans === 'Yes') yesCount++
              else noCount++
            })

            const barChart = [
              { label: 'Yes', count: yesCount, pct: (yesCount / allAnswers.length) * 100 },
              { label: 'No', count: noCount, pct: (noCount / allAnswers.length) * 100 },
            ]

            sections.push({
              title: `Q${qIdx + 1}: ${questionText}`,
              type: 'text',
              barChart,
              totalResponses: allAnswers.length,
            } as any)
          } else {
            // Text/open-ended: show all individual responses
            const textRows = scopedResponses
              .filter((r: any) => {
                const ans = (r.answers || {})[q.id]
                return ans !== undefined && ans !== null && ans !== ''
              })
              .map((r: any) => {
                const profile = profileMap.get(r.user_id)
                const respondentName = profile ? `${profile.first_name || ''} ${profile.last_name || ''}`.trim() : 'Unknown'
                const ans = (r.answers || {})[q.id]
                return [respondentName, Array.isArray(ans) ? ans.join(', ') : String(ans)]
              })

            if (textRows.length > 0) {
              sections.push({
                title: `Q${qIdx + 1}: ${questionText}`,
                type: 'table',
                data: {
                  headers: ['Respondent', 'Answer'],
                  rows: textRows,
                },
              })
            }
          }
        })
      }

      if (selectedReport === 'job_market') {
        config.title = 'Job Market Report'
        const { data: jobs, error: jobErr } = await supabaseClient
          .from('jobs')
          .select('id, title, company_name, status, employment_type, experience_level, salary_range, skills_required, posted_at, deadline')
          .is('deleted_at', null)
          .order('posted_at', { ascending: false })
        if (jobErr) throw jobErr

        sections.push({
          title: 'Job Postings',
          type: 'table',
          data: {
            headers: ['Title', 'Company', 'Type', 'Level', 'Salary', 'Status', 'Posted'],
            rows: (jobs || []).map((j: any) => [
              j.title, j.company_name || '', j.employment_type || '', j.experience_level || '',
              SALARY_LABELS[j.salary_range] || j.salary_range || '', j.status || '',
              j.posted_at ? new Date(j.posted_at).toLocaleDateString('en-PH') : '',
            ]),
          },
        })

        // Application stats
        const { data: appStats } = await supabaseClient
          .from('job_applications')
          .select('job_id, status')

        if (appStats && appStats.length > 0) {
          const jobApps: Record<string, { total: number; hired: number }> = {}
          appStats.forEach((a: any) => {
            if (!jobApps[a.job_id]) jobApps[a.job_id] = { total: 0, hired: 0 }
            jobApps[a.job_id].total++
            if (a.status === 'hired') jobApps[a.job_id].hired++
          })

          sections.push({
            title: 'Application Statistics',
            type: 'table',
            data: {
              headers: ['Job', 'Applications', 'Hired', 'Hire Rate'],
              rows: (jobs || []).filter((j: any) => jobApps[j.id]).map((j: any) => {
                const stats = jobApps[j.id]
                return [
                  j.title, stats.total, stats.hired,
                  stats.total > 0 ? `${Math.round(stats.hired / stats.total * 100)}%` : '0%',
                ]
              }),
            },
          })
        }
      }

      if (sections.length === 0 && !interventionsList?.length) {
        toast.error('No data available for the selected filters')
        setGenerating(false)
        return
      }

      // Generate and download
      const timestamp = new Date().toISOString().slice(0, 10)
      const filename = `${config.title.replace(/[^a-zA-Z0-9]/g, '_')}_${timestamp}`

      if (format === 'pdf') {
        const doc = generatePdfReport(config, sections, interventionsList)
        downloadPdf(doc, `${filename}.pdf`)
        toast.success('PDF report generated successfully')
      } else {
        const wb = await generateExcelReport(config, sections, interventionsList)
        await downloadExcel(wb, `${filename}.xlsx`)
        toast.success('Excel report generated successfully')
      }
    } catch (err: any) {
      console.error('Report generation error:', err)
      toast.error(err.message || 'Failed to generate report')
    } finally {
      setGenerating(false)
    }
  }

  // ── Render ──────────────────────────────────────────────────
  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-blue-900 rounded-2xl p-6 sm:p-8 text-white relative overflow-hidden">
        <div className="absolute -top-10 -right-10 w-52 h-52 bg-white/5 rounded-full pointer-events-none" />
        <div className="absolute -bottom-8 right-16 w-40 h-40 bg-yellow-400/10 rounded-full pointer-events-none" />
        <div className="absolute top-4 right-32 w-16 h-16 bg-white/5 rounded-full pointer-events-none" />
        <div className="relative flex items-center justify-between gap-4">
          <div className="flex items-start gap-4">
            <div className="w-10 h-10 rounded-xl bg-white/10 flex items-center justify-center shrink-0">
              <FileText className="w-5 h-5 text-white" />
            </div>
            <div>
              <h1 className="text-2xl sm:text-3xl font-bold">Generate Reports</h1>
              <p className="text-blue-200 text-sm mt-1">
                Generate professional PDF and Excel reports with university branding
              </p>
            </div>
          </div>
          {/* Logos */}
          <div className="flex items-center gap-4 shrink-0">
            <img src="/uc-logo.png" alt="University of Cebu Logo" className="h-14 w-14 object-contain brightness-0 invert" />
            <img src="/trailblaze2_logonobg.png" alt="Trailblaze Logo" className="h-10 object-contain brightness-0 invert" />
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left: Report Type Selection */}
        <div className="lg:col-span-2 space-y-6">
          {/* Analytics Reports */}
          <div>
            <h2 className="text-xs font-bold text-gray-500 uppercase tracking-wide mb-3">Analytics Reports (DSS)</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {analyticsReports.map(report => {
                const Icon = report.icon
                const isSelected = selectedReport === report.id
                return (
                  <button
                    key={report.id}
                    onClick={() => setSelectedReport(report.id)}
                    className={`text-left rounded-xl border-2 p-4 transition-all ${
                      isSelected
                        ? 'border-blue-900 bg-blue-50 shadow-md'
                        : 'border-gray-200 bg-white hover:border-blue-200 hover:shadow-sm'
                    }`}
                  >
                    <div className="flex items-start gap-3">
                      <div className={`w-9 h-9 rounded-lg flex items-center justify-center shrink-0 ${
                        isSelected ? 'bg-blue-900 text-white' : 'bg-gray-100 text-gray-500'
                      }`}>
                        <Icon className="w-4.5 h-4.5" />
                      </div>
                      <div>
                        <p className={`text-sm font-bold ${isSelected ? 'text-blue-900' : 'text-gray-800'}`}>{report.label}</p>
                        <p className="text-xs text-gray-500 mt-0.5">{report.description}</p>
                      </div>
                    </div>
                  </button>
                )
              })}
            </div>
          </div>

          {/* Database Reports */}
          <div>
            <h2 className="text-xs font-bold text-gray-500 uppercase tracking-wide mb-3">Database Reports (Administrative)</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {databaseReports.map(report => {
                const Icon = report.icon
                const isSelected = selectedReport === report.id
                return (
                  <button
                    key={report.id}
                    onClick={() => setSelectedReport(report.id)}
                    className={`text-left rounded-xl border-2 p-4 transition-all ${
                      isSelected
                        ? 'border-blue-900 bg-blue-50 shadow-md'
                        : 'border-gray-200 bg-white hover:border-blue-200 hover:shadow-sm'
                    }`}
                  >
                    <div className="flex items-start gap-3">
                      <div className={`w-9 h-9 rounded-lg flex items-center justify-center shrink-0 ${
                        isSelected ? 'bg-blue-900 text-white' : 'bg-gray-100 text-gray-500'
                      }`}>
                        <Icon className="w-4.5 h-4.5" />
                      </div>
                      <div>
                        <p className={`text-sm font-bold ${isSelected ? 'text-blue-900' : 'text-gray-800'}`}>{report.label}</p>
                        <p className="text-xs text-gray-500 mt-0.5">{report.description}</p>
                      </div>
                    </div>
                  </button>
                )
              })}
            </div>
          </div>
        </div>

        {/* Right: Filters + Generate */}
        <div className="space-y-4">
          {/* Filters Card */}
          <div className="bg-white rounded-xl border border-gray-200 p-5 space-y-4 sticky top-6 z-10">
            <div className="flex items-center gap-2">
              <Filter className="w-4 h-4 text-gray-400" />
              <h3 className="text-sm font-bold text-gray-700">Report Filters</h3>
            </div>

            {/* Department */}
            {isSuperAdmin && (
              <div>
                <label className="text-xs font-medium text-gray-500 mb-1 block">Department</label>
                <select
                  value={deptFilter || ''}
                  onChange={e => { setDeptFilter(e.target.value || null); setProgFilter(null) }}
                  className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm"
                >
                  <option value="">All Departments</option>
                  {departments.map(d => <option key={d} value={d}>{d}</option>)}
                </select>
              </div>
            )}

            {/* Program */}
            <div>
              <label className="text-xs font-medium text-gray-500 mb-1 block">Program</label>
              <select
                value={progFilter || ''}
                onChange={e => setProgFilter(e.target.value || null)}
                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm"
              >
                <option value="">All Programs</option>
                {filteredPrograms.map(p => <option key={p.code} value={p.code}>{p.code}</option>)}
              </select>
            </div>

            {/* Batch Year */}
            <div>
              <label className="text-xs font-medium text-gray-500 mb-1 block">Batch Year</label>
              <select
                value={batchFilter || ''}
                onChange={e => setBatchFilter(e.target.value ? Number(e.target.value) : null)}
                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm"
              >
                <option value="">All Years</option>
                {batchYears.map(y => <option key={y} value={y}>Class of {y}</option>)}
              </select>
            </div>

            {/* Survey Selector - only for survey_responses report */}
            {selectedReport === 'survey_responses' && (
              <SurveyDropdown
                surveys={surveys}
                value={surveyFilter}
                onChange={setSurveyFilter}
              />
            )}

            {/* Format */}
            <div>
              <label className="text-xs font-medium text-gray-500 mb-1 block">Format</label>
              <div className="flex gap-2">
                <button
                  onClick={() => setFormat('pdf')}
                  className={`flex-1 py-2 text-sm font-medium rounded-lg border transition ${
                    format === 'pdf' ? 'bg-blue-900 text-white border-blue-900' : 'bg-white text-gray-700 border-gray-200 hover:bg-gray-50'
                  }`}
                >
                  PDF
                </button>
                <button
                  onClick={() => setFormat('excel')}
                  className={`flex-1 py-2 text-sm font-medium rounded-lg border transition ${
                    format === 'excel' ? 'bg-blue-900 text-white border-blue-900' : 'bg-white text-gray-700 border-gray-200 hover:bg-gray-50'
                  }`}
                >
                  Excel
                </button>
              </div>
            </div>

            {/* Selected Report Preview */}
            {selectedReportDef && (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                <p className="text-xs font-bold text-blue-800">Selected Report</p>
                <p className="text-sm text-blue-700 mt-0.5">{selectedReportDef.label}</p>
                <p className="text-xs text-blue-500 mt-0.5">{selectedReportDef.description}</p>
              </div>
            )}

            {/* Generate Button */}
            <button
              onClick={handleGenerate}
              disabled={!selectedReport || generating}
              className="w-full flex items-center justify-center gap-2 py-3 bg-blue-900 text-white rounded-xl font-bold text-sm hover:bg-blue-800 transition disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {generating ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Generating...
                </>
              ) : (
                <>
                  <Download className="w-4 h-4" />
                  Generate {format.toUpperCase()} Report
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

export default ReportsPage
